import { useEffect } from 'preact/hooks';
import { LOCK_AFTER_MS, model } from '../app/model';
import { LEGAL_VERSION, Prefs } from '../app/prefs';
import { PAYMENT_LINK_PREFIX, tryPaymentRequest } from '../core';
import { Vault } from '../app/vault';
import { BrandLockup } from './components';
import { ActivityView } from './activity';
import { ContactEditSheet, ContactsSheet } from './contacts';
import { HomeView, ReceiveSheet, TxDetailSheet, backupState } from './home';
import { History, QRScan, Gear, Wallet } from './icons';
import { InstallFirst, InstallSheet, WebWarningsSheet, isStandalone } from './install';
import { AuthSheet, FaceIDOffer, LockScreen, PrivacyCover } from './lock';
import { BackupFlow, LanguageSetup, LegalAcceptance, LegalDocument, PinSetup, Welcome } from './onboarding';
import { ScanTab, ScannerSheet } from './scan';
import { SendSheet } from './send';
import { ExportCoreSheet, FileBackupSheet, SettingsView } from './settings';
import { type Sheet, type Tab, ui, useApp } from './state';


/**
 * Opened from a COINWOW QR code scanned with the phone's camera
 * (https://app.coinwow.ca/pay?to=…&amount=…): Send opens, already filled in,
 * as soon as the wallet is open.
 */
let launchPayment: string | null = (() => {
  if (!/^\/pay\/?$/.test(location.pathname)) return null;
  // Rebuilt from the query so it reads the same on any host (preview deploys, tests).
  const link = PAYMENT_LINK_PREFIX + location.search;
  history.replaceState(null, '', '/');
  return tryPaymentRequest(link) ? link : null;
})();
const arrivedWithPayment = launchPayment !== null;

export function App() {
  const { s } = useApp();

  useEffect(() => {
    void model.start();
    document.documentElement.lang = Prefs.currentLanguage();

    // Lock after 5 minutes without any touch, click, key or scroll — or 5 minutes away.
    let lastActivity = Date.now();
    const touch = () => { lastActivity = Date.now(); model.touch(); };
    const expired = () => Date.now() - lastActivity >= LOCK_AFTER_MS;

    // Leaving the screen (app switcher, other tab…) only hides the content; it locks
    // on return if the app stayed away 5 minutes or more.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        ui.setObscured(true);
      } else {
        if (model.phase === 'ready' && expired()) model.lock();
        else if (model.phase === 'ready') void model.refresh();
        ui.setObscured(false);
      }
    };
    const onBlur = () => { if (isStandalone()) ui.setObscured(true); };
    const onFocus = () => { if (document.visibilityState === 'visible') ui.setObscured(false); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', () => ui.setObscured(true));
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    // Silent auto-update every 30 s while the app is open (blocks are ~60 s).
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void model.refresh();
    }, 30_000);

    const activityEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];
    for (const ev of activityEvents) window.addEventListener(ev, touch, { passive: true, capture: true });
    const idleTimer = setInterval(() => {
      if (model.phase === 'ready' && expired()) model.lock();
      if (model.phase !== 'ready') lastActivity = Date.now();
    }, 5_000);
    return () => {
      clearInterval(timer);
      clearInterval(idleTimer);
      for (const ev of activityEvents) window.removeEventListener(ev, touch, { capture: true });
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Suggest adding to the Home Screen once, before creating the wallet (on iPhone the
  // Home Screen app has its own storage, separate from Safari) or after opening it.
  useEffect(() => {
    if (model.phase === 'ready' && launchPayment) {
      const link = launchPayment;
      launchPayment = null;
      ui.setTab('wallet');
      ui.open({ type: 'send', prefill: link });
    }
  }, [model.phase]);

  // Web-specific risks: shown once, as soon as the wallet is open.
  useEffect(() => {
    if (model.phase === 'ready' && !Prefs.webWarningsSeen() && !model.faceIDOfferPin && !ui.sheets.some((x) => x.type === 'webWarnings')) {
      ui.open({ type: 'webWarnings' });
    }
  }, [model.phase, model.faceIDOfferPin]);

  useEffect(() => {
    const moment = (model.phase === 'onboarding' && !model.pendingKind && Prefs.acceptedLegalVersion() !== '') || model.phase === 'ready';
    if (moment && !isStandalone() && !Prefs.installDismissed() && ui.sheets.length === 0) {
      ui.open({ type: 'install' });
    }
  }, [model.phase, model.pendingKind, Prefs.acceptedLegalVersion()]);

  let screen: preact.JSX.Element;
  if (!isStandalone() && !Prefs.installDismissed() && !Vault.exists() && !arrivedWithPayment) screen = <InstallFirst />;
  else if (Prefs.language() === null) screen = <LanguageSetup />;
  else if (Prefs.acceptedLegalVersion() !== LEGAL_VERSION) screen = <LegalAcceptance isUpdate={Prefs.acceptedLegalVersion() !== ''} />;
  else if (model.phase === 'launching') screen = <div class="screen center-screen"><BrandLockup markSize={88} /></div>;
  else if (model.phase === 'onboarding') {
    if (model.pendingKind === 'create' && !model.pendingBackedUp && model.pendingPhrase) {
      screen = <BackupFlow phrase={model.pendingPhrase} mandatory onFinish={() => model.confirmBackup()} onCancel={() => model.cancelCreate()} />;
    } else if (model.pendingKind) screen = <PinSetup restored={model.pendingKind === 'restore'} />;
    else screen = <Welcome />;
  } else if (model.phase === 'locked') screen = <LockScreen />;
  else screen = <Main />;

  return (
    <>
      {screen}
      {ui.sheets.map((sheet, i) => (
        <div class="sheet-layer" hidden={i < ui.sheets.length - 1} key={i}>{renderSheet(sheet)}</div>
      ))}
      {backupState.phrase && model.phase === 'ready' && (
        <div class="sheet-layer">
          <BackupFlow phrase={backupState.phrase} mandatory={false} onFinish={() => { Prefs.setBackupDone(true); backupState.close(); }} />
        </div>
      )}
      {model.phase === 'ready' && model.faceIDOfferPin && <div class="sheet-layer auth-layer"><FaceIDOffer /></div>}
      {model.auth && <div class="sheet-layer auth-layer"><AuthSheet /></div>}
      {ui.obscured && <PrivacyCover />}
      <span class="sr-only" aria-live="polite">{s.webFooter}</span>
    </>
  );
}

function renderSheet(sheet: Sheet) {
  switch (sheet.type) {
    case 'send': return <SendSheet prefill={sheet.prefill} />;
    case 'receive': return <ReceiveSheet />;
    case 'tx': return <TxDetailSheet item={sheet.item} />;
    case 'contacts': return <ContactsSheet pick={sheet.pick} />;
    case 'contactEdit': return <ContactEditSheet contact={sheet.contact} />;
    case 'legal': return <LegalDocument />;
    case 'exportCore': return <ExportCoreSheet />;
    case 'fileBackup': return <FileBackupSheet />;
    case 'install': return <InstallSheet />;
    case 'webWarnings': return <WebWarningsSheet />;
    case 'scanner': return <ScannerSheet onCode={sheet.onCode} />;
  }
}

function Main() {
  const { s } = useApp();
  const tabs: { id: Tab; label: string; icon: preact.JSX.Element }[] = [
    { id: 'wallet', label: s.tabWallet, icon: <Wallet size={22} /> },
    { id: 'activity', label: s.activity, icon: <History size={22} /> },
    { id: 'scan', label: s.scan, icon: <QRScan size={22} /> },
    { id: 'settings', label: s.settings, icon: <Gear size={22} /> },
  ];
  return (
    <div class="main">
      <main class="tab-content">
        {ui.tab === 'wallet' && <HomeView />}
        {ui.tab === 'activity' && <ActivityView />}
        {ui.tab === 'scan' && <ScanTab />}
        {ui.tab === 'settings' && <SettingsView />}
      </main>
      <nav class="tab-bar" aria-label="COINWOW">
        {tabs.map((t) => (
          <button type="button" class={`tab ${ui.tab === t.id ? 'on' : ''}`} aria-current={ui.tab === t.id ? 'page' : undefined}
            onClick={() => { ui.setTab(t.id); window.scrollTo({ top: 0 }); }}>
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
